import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  CircularProgress, IconButton, Menu, MenuItem, ListItemIcon, ListItemText,
  Tooltip, Box,
} from '@mui/material';
import PrintIcon from '@mui/icons-material/Print';
import TableChartIcon from '@mui/icons-material/TableChart';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import CheckIcon from '@mui/icons-material/Check';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { AppDispatch } from '../reduxStore/Store';
import { fetchTemplatesForDocument, printTemplatesKey } from '../reduxStore/ReportTemplateSlice';
import { ReportTemplate } from '../models/models';

interface Props {
  /** Génère le rapport ; templateId optionnel (null = template par défaut côté worker). */
  onPdf:      (templateId?: number | null) => void;
  onExcel:    (templateId?: number | null) => void;
  loading?:   boolean;
  disabled?:  boolean;
  /** Contexte d'impression — si fourni et que ≥2 templates existent, une flèche ▸
   *  par format permet de choisir un modèle. Le clic direct reste sur le défaut. */
  typeRapport?:      string | null;
  typeDocumentCode?: string | null;
}

type Format = 'pdf' | 'excel';

const PrintMenuButton: React.FC<Props> = ({
  onPdf, onExcel, loading = false, disabled = false, typeRapport = null, typeDocumentCode = null,
}) => {
  const dispatch = useDispatch<AppDispatch>();
  const key = printTemplatesKey(typeRapport, typeDocumentCode);
  const templates: ReportTemplate[] = useSelector((state: any) =>
    typeRapport ? (state.ReportTemplateSlice.printTemplates[key] ?? []) : []);

  const hasChoice = templates.length >= 2;

  const [anchor, setAnchor]               = useState<null | HTMLElement>(null);
  const [subAnchor, setSubAnchor]         = useState<null | HTMLElement>(null);
  const [subFormat, setSubFormat]         = useState<Format | null>(null);
  const [pending, setPending]             = useState<(() => void) | null>(null);

  const cbFor = (fmt: Format) => (fmt === 'pdf' ? onPdf : onExcel);

  const handleOpen = (e: React.MouseEvent<HTMLButtonElement>) => {
    if (loading || disabled) return;
    if (typeRapport) {
      dispatch(fetchTemplatesForDocument({ type_rapport: typeRapport, type_document_code: typeDocumentCode }));
    }
    setAnchor(e.currentTarget);
  };

  const closeAll = () => { setSubAnchor(null); setSubFormat(null); setAnchor(null); };

  /** Clic direct sur un format → impression avec le template par défaut (worker). */
  const handleDirect = (fmt: Format) => {
    const cb = cbFor(fmt);
    setPending(() => () => cb(null));
    closeAll();
  };

  /** Flèche ▸ → ouvre la liste des modèles pour ce format (sans fermer le menu). */
  const handleOpenSub = (e: React.MouseEvent<HTMLElement>, fmt: Format) => {
    e.stopPropagation();
    setSubFormat(fmt);
    setSubAnchor(e.currentTarget);
  };

  const handlePickTemplate = (templateId: number | null) => {
    const cb = cbFor(subFormat ?? 'pdf');
    setPending(() => () => cb(templateId));
    closeAll();
  };

  // L'action est déclenchée après l'animation de fermeture du menu principal
  // (onExited) → évite le warning aria-hidden sur un élément encore focalisé.
  const handleExited = () => {
    if (pending) { pending(); setPending(null); }
  };

  const formatItem = (fmt: Format, icon: React.ReactNode, label: string) => (
    <MenuItem onClick={() => handleDirect(fmt)}>
      <ListItemIcon>{icon}</ListItemIcon>
      <ListItemText>{label}</ListItemText>
      {hasChoice && (
        <Box
          component="span"
          onClick={(e) => handleOpenSub(e, fmt)}
          sx={{
            display: 'flex', alignItems: 'center', ml: 2, p: 0.75, borderRadius: 1,
            cursor: 'pointer', '&:hover': { bgcolor: 'action.selected' },
          }}
          title="Choisir un modèle"
        >
          <ChevronRightIcon fontSize="small" />
        </Box>
      )}
    </MenuItem>
  );

  return (
    <>
      <Tooltip title="Imprimer">
        {/* On ne met PAS disabled={true} sur l'IconButton pour préserver la
            couleur héritée (ex: 'white' sur l'AppBar). MUI override sinon avec
            action.disabled (rgba(0,0,0,0.26)) → invisible sur fond foncé.
            On bloque manuellement le click et on baisse l'opacité visuellement. */}
        <IconButton
          size="small"
          onClick={loading || disabled ? undefined : handleOpen}
          sx={{
            color:   'inherit',
            opacity: loading || disabled ? 0.6 : 1,
            cursor:  loading ? 'wait' : disabled ? 'not-allowed' : 'pointer',
          }}
          aria-disabled={loading || disabled}
        >
          {loading
            ? <CircularProgress size={16} color="inherit" />
            : <PrintIcon fontSize="small" />}
        </IconButton>
      </Tooltip>

      <Menu
        anchorEl={anchor}
        open={Boolean(anchor)}
        onClose={closeAll}
        TransitionProps={{ onExited: handleExited }}
      >
        {formatItem('pdf',   <PictureAsPdfIcon fontSize="small" color="error" />,   'PDF')}
        {formatItem('excel', <TableChartIcon  fontSize="small" color="success" />, 'Excel')}
      </Menu>

      {/* Sous-menu des modèles pour le format choisi via la flèche ▸ */}
      <Menu
        anchorEl={subAnchor}
        open={Boolean(subAnchor)}
        onClose={() => { setSubAnchor(null); setSubFormat(null); }}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
      >
        {templates.map((t) => (
          <MenuItem key={t.id} onClick={() => handlePickTemplate(t.id ?? null)} sx={{ gap: 2 }}>
            <ListItemText>{t.nom}</ListItemText>
            {!!t.is_default && <CheckIcon fontSize="small" color="primary" />}
          </MenuItem>
        ))}
      </Menu>
    </>
  );
};

export default PrintMenuButton;
